"use client";

import * as React from "react";
import { HelpCircle } from "lucide-react";
import { cn } from "@repo/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

export interface FieldHintProps {
  /** Hint text. Plain string or minimal ReactNode. */
  children: React.ReactNode;
  /** Which side of the icon the tooltip opens on. Default: 'top'. */
  side?: "top" | "right" | "bottom" | "left";
  /** Applied to the trigger button (rarely needed). */
  className?: string;
  /** Accessible label for the trigger. Default: 'More information'. */
  label?: string;
}

/**
 * Small inline "what is this?" icon that reveals a short explanation tooltip
 * on hover or keyboard focus. Use next to labels, table column headers, or
 * option text for fields where the meaning is not self-evident.
 */
export function FieldHint({
  children,
  side = "top",
  className,
  label = "More information",
}: FieldHintProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              "inline-flex items-center justify-center rounded-full",
              "text-muted-foreground transition-colors",
              "hover:text-foreground focus-visible:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              className
            )}
          >
            <HelpCircle className="h-3.5 w-3.5 shrink-0" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-[260px] text-sm leading-snug"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
